<template>
	<div v-if="call" @contextmenu="showContextMenu" @dblclick="pickup">
		<CallDraggable :data="enhancedCall" :type="CallDragItemType.QueueCall" :target="false" :draggable="true">
			<div class="call-box">
				<div class="position-box" :style="{ 'background': stateColor }">
					{{ index + 1 }}
				</div>
				<div class="labels">
					<div v-if="call.name && !call.privacy" class="name">{{ call.name }}</div>
					<div v-if="call.privacy" class="name">{{ $t('queue.hiddenNumber') }}</div>
					<div v-if="!call.name && !call.privacy" class="name">{{ ss.cutNationalPrefix(call.number, true) }}</div>
					<CountryFlag v-if="!call.name && !call.privacy && countryFlagName" :icon="countryFlagName" size="13" style="margin-left:3px;float:right" />
					<div class="number">
						<div v-if="call.name && !call.privacy">{{ ss.cutNationalPrefix(call.number, true) }}</div>
						<CountryFlag v-if="countryFlagName" :icon="countryFlagName" size="13" style="margin-left: 3px" />
						<div v-if="variableValue != null && call.name" style="margin-left: 5px;margin-right: 5px">•</div>
						<div v-if="variableValue != null && call.name" class="variable"><span>{{ variableName }}</span>&nbsp;{{ variableValue }}</div>
					</div>
					<div v-if="variableValue != null && !call.name" class="variable"><span>{{ variableName }}</span>&nbsp;{{ variableValue }}</div>
				</div>
				<div class="duration-box" :class="{'visible':call}">
					<div>{{ duration(currentCallDuration as number, true) }}</div>
					<Badge severity="danger" size="small" v-if="call.state === 'VIP'">
						<GlyphIcon :icon="GlyphIconName['medal']" size="14" color="var(--p-primary-contrast-color)" />
						<div>VIP</div>
					</Badge>
					<Badge severity="success" size="small" v-if="call.state === 'CALLBACK'">
						<GlyphIcon :icon="GlyphIconName['call-idle']" size="14" color="var(--p-primary-contrast-color)" />
						<div>CB</div>
					</Badge>
				</div>
			</div>
		</CallDraggable>
		<div class="bottom-line" :class="{'visible':!last}"></div>
		<ContextMenu ref="contextMenu" :model="contextMenuItems" :popup="true" />
	</div>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue'
import { callVariableKey, channelFromCallId } from '@/utilities/CallVariables'
import globalEvents from '@/utilities/GlobalEvents'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import CountryFlag from '@/components/CountryFlag.vue'
import type { PbxAppQueuesCaller } from '@/TcxApi/PbxApp'
import iso3166 from '@/utilities/iso3166'
import type ContextMenu from 'primevue/contextmenu'
import type { MenuItem } from 'primevue/menuitem'
import { duration } from '@/utilities/filters'
import { CallDragItemType } from '@/utilities/CallDrag'
import { useEmployeesStore } from '@/stores/Employees'
import GlyphIcon from '@/components/GlyphIcon.vue'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

const ss = useSettingsStore()
const api = useApiStore()
const { t } = useI18n()
const employees = useEmployeesStore()

interface Props {
	call: PbxAppQueuesCaller
	index: number
	variable?: string
	variableName?: string
	last: boolean
	serviceLevel: number
	serviceLevelCritical: number
	queueId: string
}

const props = defineProps<Props>()
const currentCallDuration = ref<number | null>(null)
const stateColor = ref('#66C566')
const variableValue = computed(() => {
	if (!props.variable || !props.call) { return undefined }
	return employees.callVariables.get(callVariableKey(channelFromCallId(props.call._id) ?? '', props.variable))
})
const contextMenuItems: MenuItem[] = [
	{ label: t('queue.action.answer'), icon: 'pi pi-phone', command: pickup },
	{ separator: true, visible: !!variableValue.value || !!(props.call.number && !props.call.privacy) },
	{ label: t('queue.action.copyVariableToClipboard').replace('%%name%%', props.variableName?.toLowerCase() ?? ''), icon: 'pi pi-clipboard', command: copyVariableToClipboard, visible: !!variableValue.value },
	{ label: t('queue.action.copyNumberToClipboard'), icon: 'pi pi-clipboard', command: copyNumberToClipboard, visible: !!props.call.number && !props.call.privacy }
]
const contextMenu = ref<InstanceType<typeof ContextMenu>>()

const countryFlagName = computed(() => {
	let number = props.call.number.substring(0, 6)
	let iso: string | null | undefined = null

	while (!iso && number) {
		number = number.slice(0, -1)
		iso = iso3166.prefix[number]
	}
	if (iso) { iso = iso.toLowerCase() }
	if (number == ss.pbxSettings.nationalPrefix) { iso = null }
	return iso
})

const enhancedCall = computed(() => {
	return { call: props.call, queueId: props.queueId }
})

function showContextMenu(event: MouseEvent) {
	event.preventDefault()
	contextMenu.value?.toggle(event)
}
async function pickup() {
	globalEvents.emit('MyCallsAutoAnswerNextRinging', props.call.sessionId)
	if (!ss.realPhoneId) { noPhoneSelected(); return }

	try {
		await api.get(`/pbx/app/action/queuepickup?device=${ss.realPhoneId}&target=${props.call._id}`)
	}
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('queue.failedAnswerHeader'), message: t('queue.failedAnswerMessage') })
	}
}

function copyVariableToClipboard() {
	if (variableValue.value) {
		navigator.clipboard.writeText(variableValue.value)
	}
}

function copyNumberToClipboard() {
	navigator.clipboard.writeText(ss.cutNationalPrefix(props.call.number))
}

function refreshDuration() {
	if (props.call) {
		currentCallDuration.value = Math.round((new Date().getTime() - props.call.entered.getTime()) / 1000)
		const seconds = Math.round((new Date().getTime() - props.call.entered.getTime()) / 1000)

		if (seconds < props.serviceLevel) {
			if (stateColor.value !== 'var(--queue-green)') { stateColor.value = 'var(--queue-green)' }
		} else if (seconds < props.serviceLevelCritical) {
			if (stateColor.value !== 'var(--queue-yellow)') { stateColor.value = 'var(--queue-yellow)' }
		} else if (stateColor.value !== 'var(--queue-red)') { stateColor.value = 'var(--queue-red)' }
	} else {
		currentCallDuration.value = 0
		stateColor.value = 'var(--queue-green)'
	}
}

function noPhoneSelected() {
	globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.error.noPhoneMessage') })
}

globalEvents.on('OneSecond', refreshDuration)
setTimeout(() => { refreshDuration() }, 100)

onBeforeUnmount(() => {
	globalEvents.off('OneSecond', refreshDuration)
})

// CALL_VARIABLE events are transient, so a client that was disconnected when the IVR set the value
// never received it. Resolve through the store, which fetches what it is missing and retries after a reconnect.
watchEffect(() => {
	employees.ensureCallVariable(props.call._id, props.variable)
})
</script>

<style scoped>
	.call-box {
		display: grid;
		grid-template-columns: 44px auto 65px;
		overflow: hidden;
		transition: 400ms;
		transition-delay: 1s;
		padding: 0;
		height: 40px;
	}
	.position-box {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		color: var(--queue-position);
		text-align: center;
		font-size: 20px;
		font-weight: 200;
		padding-top: 5px;
		margin-left: 4px;
	}
	.labels {
		padding-left: 8px;
		height: 40px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		min-width: 0;
	}
	.labels DIV {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.name {
		font-size: 14px;
		font-weight: 300;
		line-height: 16px;
		letter-spacing: 0.01em;
	}
	.number {
		font-size: 11px;
		color: var(--label-message-color);
		line-height: 12px;
		display: inline-flex;
		letter-spacing: 0.01em;
	}
	.variable {
		font-size: 11px;
		font-style: italic;
		color: var(--label-location-color);
		line-height: 12px;
		display: inline-flex;
		transition: 400ms;
	}
	.variable > SPAN {
		font-weight: bold;
		margin-right: 5px;
	}
	.duration-box {
		height: 40px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		float: right;
		transition: 600ms;
		transition-delay: 300ms;
		max-width: 0px;
		min-width: 0px;
		overflow: hidden;
		margin-right: 4px;
	}
	.duration-box.visible {
		max-width: 65px;
		min-width: 40px;
	}
	.duration-box > DIV {
		color: var(--p-text-muted-color);
		font-family: RobotoMonoThin;
		font-size: 11px;
		flex-shrink: 0;
		text-align: right;
	}
	.bottom-line {
		margin-left: 52px;
		margin-top: 2px;
		margin-bottom: 2px;
		border-bottom: 1px solid var(--p-form-field-border-color);
		opacity: 0;
		transition: 600ms;
		transition-delay: 200ms;
	}
	.bottom-line.visible {
		opacity: 0.5;
	}

</style>
